/**
 * Stack - Store Locator
 * Web Component for finding pharmacies on an interactive map.
 * Uses Leaflet.js. Basemap tiles from standard OpenStreetMap (free, no API key).
 * Geocoding uses MapTiler when an API key is configured, otherwise Nominatim (OSM).
 */

class StoreLocator extends HTMLElement {
  constructor() {
    super();
    this.allStores = [];
    this.map = null;
    this.markerLayer = null;
    this.markers = {};
    this.searchQuery = '';
    this.selectedRadius = null;
    this.geocodedCenter = null;
    this.lastCenter = null;
    this.lastRadius = null;
    this.searchTimeout = null;
    this.geocodeTimeout = null;
    this.activeCardId = null;
  }

  connectedCallback() {
    this.searchInput = this.querySelector('.stack-store-locator__search');
    this.radiusSelect = this.querySelector('.stack-store-locator__radius');
    this.mapContainer = this.querySelector('.stack-store-locator__map');
    this.listContainer = this.querySelector('.stack-store-locator__list');
    this.countContainer = this.querySelector('.stack-store-locator__count');
    this.messageContainer = this.querySelector('.stack-store-locator__message');
    this.loadingContainer = this.querySelector('.stack-store-locator__loading');
    this.geolocateBtn = this.querySelector('.stack-store-locator__geolocate');
    this.clearBtn = this.querySelector('.stack-store-locator__clear');

    // Cache marker icon URL from data attribute
    this.markerIconUrl = this.getAttribute('data-marker-icon') || '';

    // API config (configurable from the section schema in the theme editor)
    this.apiUrl = (this.dataset.apiUrl || '').trim();
    this.apiDefaultRadius = parseFloat(this.dataset.apiRadius) || 50;
    this.apiLimit = parseInt(this.dataset.apiLimit, 10) || 50;
    this.fallbackJsonUrl = this.dataset.fallbackJsonUrl || '';

    // MapTiler key, used only for geocoding (address search), not for map tiles.
    this.maptilerKey = (this.dataset.maptilerKey || '').trim();

    // "Search this area" button
    this.searchAreaBtn = this.querySelector('.stack-store-locator__search-area');
    this._moveEndTimeout = null;

    // Autocomplete state
    this.acIndex = -1;

    // Flag to suppress moveend during initialization
    this._initializing = true;

    this.initMap();
    this.createAutocompleteDropdown();
    this.bindEvents();

    // Single initial load: try geolocation first, fallback to map center
    if ('geolocation' in navigator) {
      this.handleGeolocate();
    } else {
      const lat = parseFloat(this.dataset.centerLat) || 40.4168;
      const lng = parseFloat(this.dataset.centerLng) || -3.7038;
      this.loadStoresAt(lat, lng, this.apiDefaultRadius, this.apiLimit, { fitBounds: true }).then(() => {
        this._initializing = false;
      });
    }
  }

  /* ── Map ── */

  initMap() {
    if (typeof L === 'undefined') {
      this.showMessage('El mapa no está disponible. Puedes buscar farmacias en la lista.', true);
      return;
    }

    // If we know where the user was last time (geolocation / search on a previous
    // visit), start centered there instead of flying in from the whole-country view.
    const cached = this.readCachedCenter();
    const lat = cached ? cached.lat : (parseFloat(this.dataset.centerLat) || 40.4168);
    const lng = cached ? cached.lng : (parseFloat(this.dataset.centerLng) || -3.7038);
    const zoom = cached ? 13 : (parseInt(this.dataset.initialZoom) || 6);

    this.map = L.map(this.mapContainer, {
      center: [lat, lng],
      zoom: zoom,
      scrollWheelZoom: true,
      minZoom: 5
    });

    // Standard OpenStreetMap basemap tiles: free, no API key, no request quota.
    // (Wikimedia's osm-intl tiles were tried as a less-cluttered alternative, but
    // their servers reject any request carrying a non-Wikimedia Referer header
    // with 403 — i.e. they block hotlinking from external sites like this one.)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      subdomains: 'abc',
      maxZoom: 19
    }).addTo(this.map);

    this.markerLayer = (typeof L.markerClusterGroup === 'function')
      ? L.markerClusterGroup({
          maxClusterRadius: 50,
          spiderfyOnMaxZoom: true,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,
          disableClusteringAtZoom: 15
        })
      : L.layerGroup();
    this.map.addLayer(this.markerLayer);
  }

  /* ── Data Loading (API) ── */

  /**
   * Consulta la API de farmacias cercanas a un punto (lat/lng).
   * Ver docs/SHOPIFY-INTEGRATION.md para el contrato de la API.
   * Si la API falla, cae al JSON estático (`stores.json`) como red de seguridad.
   */
  async loadStoresAt(lat, lng, radius, limit, opts = {}) {
    if (isNaN(lat) || isNaN(lng)) return;

    this.lastCenter = { lat, lng };
    this.lastRadius = radius;
    this.toggleLoading(true);
    this.hideMessage();

    try {
      const stores = await this.fetchStoresFromApi(lat, lng, radius, limit);
      this.allStores = stores;
      this.render(opts);
    } catch (e) {
      console.error('Store Locator: Error consultando la API, usando fallback', e);
      try {
        const stores = await this.loadFallbackStores(lat, lng, radius, limit);
        this.allStores = stores;
        this.render(opts);
        if (stores.length === 0) {
          this.showMessage('No se pudieron cargar las tiendas. Inténtalo de nuevo más tarde.', true);
        }
      } catch (fallbackError) {
        console.error('Store Locator: Error cargando el fallback', fallbackError);
        this.showMessage('No se pudieron cargar las tiendas. Inténtalo de nuevo más tarde.', true);
      }
    } finally {
      this.toggleLoading(false);
    }
  }

  /**
   * GET {apiUrl}?lat=&lng=&radius=&limit= → array de tiendas ordenado por distancia.
   */
  async fetchStoresFromApi(lat, lng, radius, limit) {
    if (!this.apiUrl) throw new Error('No se ha configurado la URL de la API de tiendas.');

    const params = new URLSearchParams({
      lat: String(lat),
      lng: String(lng),
      radius: String(radius || this.apiDefaultRadius),
      limit: String(limit || this.apiLimit)
    });

    const res = await fetch(`${this.apiUrl}?${params}`);
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    if (!Array.isArray(data)) throw new Error('Formato de datos inválido');

    return data
      .filter(s => StoreLocator.isValidCoord(s))
      .map((s, i) => ({ ...s, id: s.id || `store-${i}`, _distance: typeof s.distance === 'number' ? s.distance : undefined }));
  }

  /**
   * Fallback: `assets/stores.json` estático, filtrado y ordenado por distancia en cliente.
   */
  async loadFallbackStores(lat, lng, radius, limit) {
    if (!this.fallbackJsonUrl) return [];

    const res = await fetch(this.fallbackJsonUrl);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (!Array.isArray(data)) throw new Error('Formato de datos inválido');

    return data
      .filter(s => StoreLocator.isValidCoord(s))
      .map((s, i) => ({
        ...s,
        id: s.id || `store-${i}`,
        _distance: StoreLocator.haversine(lat, lng, parseFloat(s.lat), parseFloat(s.lng))
      }))
      .filter(s => !isNaN(s._distance) && s._distance <= (radius || this.apiDefaultRadius))
      .sort((a, b) => a._distance - b._distance)
      .slice(0, limit || this.apiLimit);
  }

  /**
   * Pinta en el mapa y en la lista el resultado actual de `this.allStores`
   * (la API ya lo devuelve ordenado por distancia, sin necesidad de filtrar en cliente).
   */
  render(opts = {}) {
    const stores = this.allStores;

    if (opts.center) {
      this._skipMoveEnd = true;
      // No fly-in animation on the very first load — appear already centered.
      this.map.setView([opts.center.lat, opts.center.lng], opts.zoom || 13, { animate: !this._initializing });
      this.updateMap(stores, true);
    } else {
      this.updateMap(stores, !opts.fitBounds ? true : false);
    }

    this.updateResults(stores);
    this.announceResults(stores.length);
  }

  /* ── Events ── */

  createAutocompleteDropdown() {
    if (!this.searchInput) return;
    this.acDropdown = document.createElement('div');
    this.acDropdown.className = 'stack-store-locator__autocomplete';
    this.acDropdown.setAttribute('role', 'listbox');
    this.acDropdown.style.display = 'none';
    this.searchInput.parentNode.appendChild(this.acDropdown);
    this.searchInput.setAttribute('role', 'combobox');
    this.searchInput.setAttribute('aria-autocomplete', 'list');
    this.searchInput.setAttribute('aria-expanded', 'false');
  }

  updateAutocomplete() {
    if (!this.acDropdown || !this.searchInput) return;
    const query = this.searchInput.value.trim();
    if (query.length < 2) {
      this.hideAutocomplete();
      return;
    }

    clearTimeout(this.acFetchTimeout);
    this.acFetchTimeout = setTimeout(() => {
      this.fetchSuggestions(query)
        .then(suggestions => {
          if (!suggestions || suggestions.length === 0) {
            this.hideAutocomplete();
            return;
          }

          this.acDropdown.innerHTML = suggestions.map((s, i) =>
            `<div class="stack-store-locator__ac-item${i === this.acIndex ? ' is-active' : ''}" role="option" data-index="${i}">
              <span class="stack-store-locator__ac-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg></span>
              <span class="stack-store-locator__ac-text">
                <span class="stack-store-locator__ac-label">${this.escapeHtml(s.label)}</span>
                ${s.sublabel ? '<span class="stack-store-locator__ac-sub">' + this.escapeHtml(s.sublabel) + '</span>' : ''}
              </span>
            </div>`
          ).join('');

          this.acDropdown.style.display = 'block';
          this.searchInput.setAttribute('aria-expanded', 'true');
          this.acSuggestions = suggestions;

          this.acDropdown.querySelectorAll('.stack-store-locator__ac-item').forEach(item => {
            item.addEventListener('mousedown', (e) => {
              e.preventDefault();
              this.selectAutocomplete(parseInt(item.dataset.index));
            });
          });
        })
        .catch(() => this.hideAutocomplete());
    }, 300);
  }

  /* ── Geocoding provider ── */
  // Uses MapTiler Geocoding when an API key is configured, otherwise Nominatim (OSM).

  get geocoderKey() {
    return this.maptilerKey || '';
  }

  async fetchSuggestions(query) {
    if (this.geocoderKey) {
      const proximity = this.map
        ? `&proximity=${this.map.getCenter().lng},${this.map.getCenter().lat}`
        : '';
      const url = `https://api.maptiler.com/geocoding/${encodeURIComponent(query)}.json?key=${encodeURIComponent(this.geocoderKey)}&language=es&country=es&limit=6&autocomplete=true${proximity}`;
      const res = await fetch(url);
      const data = await res.json();
      const feats = (data && data.features) || [];
      const seen = new Set();
      const out = [];
      for (const f of feats) {
        const label = this.buildMaptilerLabel(f);
        const key = label.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
          label: label,
          sublabel: this.buildMaptilerSublabel(f),
          lat: f.center[1],
          lng: f.center[0]
        });
      }
      return out;
    }

    // Nominatim fallback
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=6&addressdetails=1`;
    const res = await fetch(url, { headers: { 'Accept-Language': 'es' } });
    const data = await res.json();
    if (!data || data.length === 0) return [];
    const seen = new Set();
    const out = [];
    for (const item of data) {
      const addr = item.address || {};
      const label = this.buildNominatimLabel(item, addr);
      const key = label.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        label: label,
        sublabel: this.buildNominatimSublabel(addr),
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon)
      });
    }
    return out;
  }

  buildMaptilerLabel(f) {
    // f.text is the primary name (city, postcode, street…)
    const types = f.place_type || [];
    if (types.includes('postal_code')) {
      const city = (f.context || []).find(c => /place|municipality|city/.test(c.id || ''));
      return f.text + (city ? ', ' + city.text : '');
    }
    return f.text || (f.place_name || '').split(',').slice(0, 2).join(',');
  }

  buildMaptilerSublabel(f) {
    const parts = (f.context || [])
      .filter(c => /region|country/.test(c.id || ''))
      .map(c => c.text);
    return parts.join(', ');
  }

  buildNominatimLabel(item, addr) {
    // For postcodes, show the postcode + city
    if (item.type === 'postcode' || item.class === 'place' && addr.postcode) {
      const city = addr.city || addr.town || addr.village || '';
      return addr.postcode ? addr.postcode + (city ? ', ' + city : '') : item.display_name.split(',')[0];
    }
    // For cities/towns
    if (addr.city || addr.town || addr.village) {
      return addr.city || addr.town || addr.village;
    }
    return item.display_name.split(',').slice(0, 2).join(',');
  }

  buildNominatimSublabel(addr) {
    const parts = [addr.state, addr.country].filter(Boolean);
    return parts.join(', ');
  }

  selectAutocomplete(index) {
    if (!this.acSuggestions || !this.acSuggestions[index]) return;
    const suggestion = this.acSuggestions[index];
    this.searchInput.value = suggestion.label;
    if (this.clearBtn) this.clearBtn.style.display = 'flex';
    this.hideAutocomplete();

    // Set geocoded center directly from the suggestion coords and re-query the API
    this.geocodedCenter = { lat: suggestion.lat, lng: suggestion.lng };
    this.writeCachedCenter(suggestion.lat, suggestion.lng);
    this.searchQuery = suggestion.label;
    this.loadStoresAt(
      suggestion.lat,
      suggestion.lng,
      this.selectedRadius || 100,
      this.apiLimit,
      { center: { lat: suggestion.lat, lng: suggestion.lng }, zoom: 13 }
    );
  }

  hideAutocomplete() {
    if (!this.acDropdown) return;
    this.acDropdown.style.display = 'none';
    this.acDropdown.innerHTML = '';
    this.acIndex = -1;
    this.acSuggestions = null;
    if (this.searchInput) this.searchInput.setAttribute('aria-expanded', 'false');
  }

  bindEvents() {
    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.updateAutocomplete();
        }, 300);
        if (this.clearBtn) {
          this.clearBtn.style.display = this.searchInput.value.length > 0 ? 'flex' : 'none';
        }
      });

      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          if (this.acDropdown && this.acDropdown.style.display !== 'none') {
            this.hideAutocomplete();
          } else {
            this.searchInput.value = '';
            if (this.clearBtn) this.clearBtn.style.display = 'none';
            this.handleSearch();
          }
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (this.acSuggestions && this.acSuggestions.length > 0) {
            this.acIndex = Math.min(this.acIndex + 1, this.acSuggestions.length - 1);
            this.highlightAcItem();
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (this.acSuggestions && this.acSuggestions.length > 0) {
            this.acIndex = Math.max(this.acIndex - 1, 0);
            this.highlightAcItem();
          }
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (this.acIndex >= 0 && this.acSuggestions) {
            this.selectAutocomplete(this.acIndex);
          } else {
            this.hideAutocomplete();
            this.handleSearch();
          }
        }
      });

      this.searchInput.addEventListener('blur', () => {
        setTimeout(() => this.hideAutocomplete(), 150);
      });

      this.searchInput.addEventListener('focus', () => {
        if (this.searchInput.value.trim().length >= 2) {
          this.updateAutocomplete();
        }
      });
    }

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => {
        this.searchInput.value = '';
        this.clearBtn.style.display = 'none';
        this.hideAutocomplete();
        this.searchInput.focus();
        this.handleSearch();
      });
    }

    if (this.radiusSelect) {
      this.radiusSelect.addEventListener('change', () => this.handleRadiusChange());
    }

    if (this.geolocateBtn) {
      if ('geolocation' in navigator) {
        this.geolocateBtn.addEventListener('click', () => this.handleGeolocate());
      } else {
        this.geolocateBtn.style.display = 'none';
      }
    }

    // "Search this area" — shows when user pans/zooms the map
    if (this.map && this.searchAreaBtn) {
      this.searchAreaBtn.addEventListener('click', () => this.searchInMapArea());

      this.map.on('moveend', () => {
        if (this._skipMoveEnd) { this._skipMoveEnd = false; return; }
        if (this._initializing) return;
        this.showSearchAreaBtn();
        // Auto-search after 2 seconds of inactivity
        clearTimeout(this._moveEndTimeout);
        this._moveEndTimeout = setTimeout(() => this.searchInMapArea(), 2000);
      });
    }

  }

  /* ── Search & Filter ── */

  highlightAcItem() {
    if (!this.acDropdown) return;
    this.acDropdown.querySelectorAll('.stack-store-locator__ac-item').forEach((el, i) => {
      el.classList.toggle('is-active', i === this.acIndex);
    });
  }

  handleSearch() {
    this.searchQuery = this.searchInput ? this.searchInput.value.trim() : '';
    this.hideMessage();

    if (!this.searchQuery) {
      this.geocodedCenter = null;
      // Volver a la ubicación por defecto (última ubicación conocida del usuario, o centro del mapa)
      const lat = this.lastCenter ? this.lastCenter.lat : (parseFloat(this.dataset.centerLat) || 40.4168);
      const lng = this.lastCenter ? this.lastCenter.lng : (parseFloat(this.dataset.centerLng) || -3.7038);
      this.loadStoresAt(lat, lng, this.apiDefaultRadius, this.apiLimit, { fitBounds: true });
      return;
    }

    // Geocodificar la búsqueda y re-consultar la API con las nuevas coordenadas
    this.geocode(this.searchQuery).then(() => {
      this.queryApiFromCurrentState();
    });
  }

  handleRadiusChange() {
    const val = this.radiusSelect ? this.radiusSelect.value : '';
    this.selectedRadius = val ? parseFloat(val) : null;
    this.hideMessage();

    if (this.selectedRadius && !this.searchQuery) {
      this.showMessage('Introduce una ubicación para filtrar por distancia.');
      return;
    }

    if (this.selectedRadius && this.searchQuery) {
      this.geocode(this.searchQuery).then(() => this.queryApiFromCurrentState());
    } else {
      this.geocodedCenter = null;
      this.queryApiFromCurrentState();
    }
  }

  /**
   * Re-consulta la API usando el centro geocodificado actual (o el último centro conocido)
   * y el radio seleccionado por el usuario.
   */
  queryApiFromCurrentState() {
    const center = this.geocodedCenter || this.lastCenter;
    if (!center) return;

    const radius = this.selectedRadius || 100;
    this.loadStoresAt(center.lat, center.lng, radius, this.apiLimit, {
      center: this.geocodedCenter ? { lat: center.lat, lng: center.lng } : undefined,
      zoom: 13
    });
  }

  /* ── Search in visible map area ── */

  showSearchAreaBtn() {
    if (this.searchAreaBtn) {
      this.searchAreaBtn.style.display = 'flex';
      this.searchAreaBtn.classList.remove('is-loading');
    }
  }

  hideSearchAreaBtn() {
    if (this.searchAreaBtn) {
      this.searchAreaBtn.style.display = 'none';
      this.searchAreaBtn.classList.remove('is-loading');
    }
  }

  /**
   * Triggered when user clicks the button or after the 2s auto-timer.
   * Uses the current map center to query the API.
   */
  async searchInMapArea() {
    if (!this.map) return;
    clearTimeout(this._moveEndTimeout);

    const center = this.map.getCenter();

    // Calculate radius from visible bounds (diagonal / 2 capped at 200 km)
    const bounds = this.map.getBounds();
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const diagonalKm = StoreLocator.haversine(sw.lat, sw.lng, ne.lat, ne.lng);
    const radius = Math.min(Math.round(diagonalKm / 2), 200) || this.apiDefaultRadius;

    // Show loading state on the button
    if (this.searchAreaBtn) {
      this.searchAreaBtn.classList.add('is-loading');
    }

    this.lastCenter = { lat: center.lat, lng: center.lng };
    this.lastRadius = radius;
    this.hideMessage();

    try {
      const stores = await this.fetchStoresFromApi(center.lat, center.lng, radius, this.apiLimit);
      this.allStores = stores;
      this.updateMap(stores, true); // don't move the map
      this.updateResults(stores);
      this.announceResults(stores.length);
    } catch (e) {
      console.error('Store Locator: Error buscando en esta zona', e);
      try {
        const stores = await this.loadFallbackStores(center.lat, center.lng, radius, this.apiLimit);
        this.allStores = stores;
        this.updateMap(stores, true);
        this.updateResults(stores);
        this.announceResults(stores.length);
      } catch (fallbackErr) {
        console.error('Store Locator: Error con fallback', fallbackErr);
      }
    } finally {
      this.hideSearchAreaBtn();
    }
  }

  /* ── Geocoding ── */

  async geocode(query) {
    try {
      let center = null;
      if (this.geocoderKey) {
        const url = `https://api.maptiler.com/geocoding/${encodeURIComponent(query)}.json?key=${encodeURIComponent(this.geocoderKey)}&language=es&country=es&limit=1`;
        const res = await fetch(url);
        const data = await res.json();
        const f = data && data.features && data.features[0];
        if (f) center = { lat: f.center[1], lng: f.center[0] };
      } else {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`;
        const res = await fetch(url, { headers: { 'Accept-Language': 'es' } });
        const data = await res.json();
        if (data.length > 0) center = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      }

      if (center) {
        this.geocodedCenter = center;
        this.writeCachedCenter(center.lat, center.lng);
      } else {
        this.geocodedCenter = null;
        this.showMessage('No se pudo encontrar la ubicación. Prueba con otro término de búsqueda.');
      }
    } catch (e) {
      console.error('Store Locator: Geocoding error', e);
      this.geocodedCenter = null;
    }
  }

  /* ── Map Rendering ── */

  updateMap(stores, skipFitBounds = false) {
    if (!this.map || !this.markerLayer) return;

    this.markerLayer.clearLayers();
    this.markers = {};

    const markerIconUrl = this.markerIconUrl || '';
    const markerColor = this.getAttribute('data-marker-color') || '#e63946';

    let icon;
    if (markerIconUrl) {
      icon = L.icon({
        iconUrl: markerIconUrl,
        iconSize: [27, 40],
        iconAnchor: [13, 40],
        popupAnchor: [0, -40]
      });
    } else {
      icon = L.divIcon({
        className: 'ssl-marker',
        html: `<svg width="24" height="36" viewBox="0 0 24 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="${markerColor}"/>
          <circle cx="12" cy="12" r="5" fill="white"/>
        </svg>`,
        iconSize: [24, 36],
        iconAnchor: [12, 36],
        popupAnchor: [0, -36]
      });
    }

    stores.forEach(store => {
      const lat = parseFloat(store.lat);
      const lng = parseFloat(store.lng);
      if (isNaN(lat) || isNaN(lng)) return;

      const popupContent = this.buildPopupContent(store);
      const marker = L.marker([lat, lng], { icon })
        .bindPopup(popupContent, { className: 'ssl-popup', maxWidth: 250 });

      marker.on('click', () => {
        this.highlightCard(store.id);
      });

      this.markers[store.id] = marker;
      this.markerLayer.addLayer(marker);
    });

    // Fit bounds (skip when updating from viewport move)
    if (!skipFitBounds) {
      if (stores.length > 1) {
        const validCoords = stores
          .map(s => [parseFloat(s.lat), parseFloat(s.lng)])
          .filter(c => !isNaN(c[0]) && !isNaN(c[1]));
        if (validCoords.length > 0) {
          this._skipMoveEnd = true;
          this.map.fitBounds(validCoords, { padding: [30, 30], maxZoom: 14 });
        }
      } else if (stores.length === 1) {
        const s = stores[0];
        this._skipMoveEnd = true;
        this.map.setView([parseFloat(s.lat), parseFloat(s.lng)], 14);
      }
    }
  }

  buildPopupContent(store) {
    let html = `<div class="ssl-popup__inner">`;
    html += `<div class="ssl-popup__name">${this.escapeHtml(store.store)}</div>`;
    html += `<div class="ssl-popup__details">`;
    if (store.address) html += `<div class="ssl-popup__row"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg><span>${this.escapeHtml(store.address)}</span></div>`;
    if (store.city || store.zip) {
      html += `<div class="ssl-popup__row ssl-popup__row--sub"><span>${this.escapeHtml(store.zip)} ${this.escapeHtml(store.city)}</span></div>`;
    }
    if (store.phone) html += `<div class="ssl-popup__row"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg><a href="tel:${store.phone}">${this.escapeHtml(store.phone)}</a></div>`;
    if (store.hours) html += `<div class="ssl-popup__row"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg><span>${this.escapeHtml(store.hours)}</span></div>`;
    html += `</div></div>`;
    return html;
  }

  /* ── Results List ── */

  updateResults(stores) {
    if (!this.listContainer || !this.countContainer) return;

    // Count
    this.countContainer.textContent = `${stores.length} farmacia${stores.length !== 1 ? 's' : ''} encontrada${stores.length !== 1 ? 's' : ''}`;

    // List
    if (stores.length === 0) {
      this.listContainer.innerHTML = `<div class="stack-store-locator__no-results">No se encontraron farmacias con los criterios actuales.</div>`;
      return;
    }

    this.listContainer.innerHTML = stores.map(store => {
      let distanceHtml = '';
      if (store._distance != null) {
        distanceHtml = `<div class="stack-store-locator__card-distance">${store._distance.toFixed(1)} km</div>`;
      }

      return `
        <div class="stack-store-locator__card" role="listitem" tabindex="0" data-store-id="${store.id}">
          <div class="stack-store-locator__card-name">${this.escapeHtml(store.store)}</div>
          <div class="stack-store-locator__card-address">${this.escapeHtml(store.address)}${store.address2 ? ', ' + this.escapeHtml(store.address2) : ''}</div>
          <div class="stack-store-locator__card-city">${this.escapeHtml(store.zip)} ${this.escapeHtml(store.city)}${store.state ? ', ' + this.escapeHtml(store.state) : ''}</div>
          ${store.phone ? `<div class="stack-store-locator__card-phone">Tel: ${this.escapeHtml(store.phone)}</div>` : ''}
          ${store.hours ? `<div class="stack-store-locator__card-hours">${this.escapeHtml(store.hours)}</div>` : ''}
          ${distanceHtml}
        </div>
      `;
    }).join('');

    // Bind card clicks
    this.listContainer.querySelectorAll('.stack-store-locator__card').forEach(card => {
      const handler = () => {
        const id = card.dataset.storeId;
        this.focusStore(id);
      };
      card.addEventListener('click', handler);
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handler();
        }
      });
    });
  }

  /* ── Interaction ── */

  focusStore(id) {
    const marker = this.markers[id];
    if (marker && this.map) {
      this.map.setView(marker.getLatLng(), 17);
      marker.openPopup();
    }
    this.highlightCard(id);
  }

  highlightCard(id) {
    // Remove previous active
    const prev = this.listContainer.querySelector('.stack-store-locator__card.is-active');
    if (prev) prev.classList.remove('is-active');

    // Set new active
    const card = this.listContainer.querySelector(`[data-store-id="${id}"]`);
    if (card) {
      card.classList.add('is-active');
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    this.activeCardId = id;
  }

  announceResults(count) {
    if (!this.countContainer) return;
    const display = Math.min(count, 50);
    this.countContainer.textContent = `${display} farmacia${display !== 1 ? 's' : ''} encontrada${display !== 1 ? 's' : ''}`;
  }

  /* ── Utilities ── */

  static haversine(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  static isValidCoord(store) {
    const lat = parseFloat(store.lat);
    const lng = parseFloat(store.lng);
    if (isNaN(lat) || isNaN(lng)) return false;
    // Bounding box for Spain (peninsula + Baleares + Canarias)
    return lat >= 27 && lat <= 44 && lng >= -19 && lng <= 5;
  }

  escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* Remember the last known map center (per browser) so repeat visits start there. */
  readCachedCenter() {
    try {
      const o = JSON.parse(localStorage.getItem('ssl:lastCenter') || 'null');
      if (o && isFinite(o.lat) && isFinite(o.lng)) return { lat: o.lat, lng: o.lng };
    } catch (e) {}
    return null;
  }

  writeCachedCenter(lat, lng) {
    try {
      localStorage.setItem('ssl:lastCenter', JSON.stringify({ lat: lat, lng: lng }));
    } catch (e) {}
  }

  /* ── UI Helpers ── */

  showMessage(text, isError = false) {
    if (!this.messageContainer) return;
    this.messageContainer.textContent = text;
    this.messageContainer.style.display = 'block';
    this.messageContainer.classList.toggle('stack-store-locator__message--error', isError);
  }

  hideMessage() {
    if (!this.messageContainer) return;
    this.messageContainer.style.display = 'none';
  }

  /* ── Geolocation ── */

  handleGeolocate() {
    if (!this.geolocateBtn) return;
    this.geolocateBtn.classList.add('is-loading');
    this.hideMessage();

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.geolocateBtn.classList.remove('is-loading');
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        // Store as geocoded center for radius filtering
        this.geocodedCenter = { lat, lng };
        this.writeCachedCenter(lat, lng);

        // Add/update user marker
        if (this.userMarker) {
          this.userMarker.setLatLng([lat, lng]);
        } else if (this.map) {
          this.userMarker = L.circleMarker([lat, lng], {
            radius: 8,
            fillColor: '#4285f4',
            fillOpacity: 1,
            color: '#fff',
            weight: 2
          }).addTo(this.map).bindPopup('Tu ubicación');
        }

        // Reverse geocode to fill search input
        this.reverseGeocode(lat, lng);

        // Single API call with user's location
        this.loadStoresAt(lat, lng, this.apiDefaultRadius, this.apiLimit, {
          center: { lat, lng },
          zoom: 13
        }).then(() => {
          this._initializing = false;
        });
      },
      (err) => {
        this.geolocateBtn.classList.remove('is-loading');
        if (err.code === 1) {
          this.showMessage('Has denegado el acceso a tu ubicación. Puedes buscarlo manualmente.');
        } else {
          this.showMessage('No se pudo obtener tu ubicación. Inténtalo de nuevo.');
        }
        // Fallback: if this was the initial load, use the last known center
        // (previous visit) or the section's default center.
        if (this._initializing) {
          const cached = this.readCachedCenter();
          if (cached) {
            this.geocodedCenter = cached;
            this.loadStoresAt(cached.lat, cached.lng, this.apiDefaultRadius, this.apiLimit, {
              center: cached,
              zoom: 13
            }).then(() => { this._initializing = false; });
          } else {
            const lat = parseFloat(this.dataset.centerLat) || 40.4168;
            const lng = parseFloat(this.dataset.centerLng) || -3.7038;
            this.loadStoresAt(lat, lng, this.apiDefaultRadius, this.apiLimit, { fitBounds: true }).then(() => {
              this._initializing = false;
            });
          }
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }

  async reverseGeocode(lat, lng) {
    try {
      let shortAddr = '';
      if (this.geocoderKey) {
        const url = `https://api.maptiler.com/geocoding/${lng},${lat}.json?key=${encodeURIComponent(this.geocoderKey)}&language=es`;
        const res = await fetch(url);
        const data = await res.json();
        const f = data && data.features && data.features[0];
        if (f) shortAddr = f.place_name || f.text || '';
      } else {
        const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16&addressdetails=1`;
        const res = await fetch(url, { headers: { 'Accept-Language': 'es' } });
        const data = await res.json();
        if (data && data.display_name) {
          const addr = data.address || {};
          const parts = [addr.road, addr.city || addr.town || addr.village, addr.postcode].filter(Boolean);
          shortAddr = parts.length > 0 ? parts.join(', ') : data.display_name.split(',').slice(0, 3).join(',');
        }
      }

      if (shortAddr && this.searchInput) {
        this.searchInput.value = shortAddr;
        this.searchQuery = shortAddr;
        if (this.clearBtn) this.clearBtn.style.display = 'flex';
      }
    } catch (e) {
      console.error('Store Locator: Reverse geocoding error', e);
    }
  }

  toggleLoading(show) {
    if (this.loadingContainer) {
      this.loadingContainer.style.display = show ? 'block' : 'none';
    }
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
